/**
 * Books Health Check — deterministic reconciliation rules over the whole
 * dataset. No LLM, no guessing: each rule is plain arithmetic that either ties
 * out or doesn't, so results are reproducible and auditable. This is the safety
 * net for the accounting engine — run it anytime, especially before tax export.
 *
 * Severity:
 *   error — numbers are wrong / data is corrupt; fix before trusting reports.
 *   warn  — likely a mistake or something to review.
 *   info  — worth knowing; not necessarily wrong.
 */
import { DB } from "./db";
import { isBulkPoolRow } from "./bulk";
import { bookedBasisForRipPulls } from "./ripBasis";
import { cashOnHand, ownerLoanBalance } from "./derived";
import { fmt$ } from "../utils/format";

export type Severity = "error" | "warn" | "info";
export interface HealthIssue {
  severity: Severity;
  category: string;
  message: string;
  ref?: string;
}

const TOL = 0.02;

function dupCheck(rows: any[], key: string, label: string, push: (i: HealthIssue) => void): void {
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const id = r[key];
    if (!id) continue;
    counts[id] = (counts[id] || 0) + 1;
  }
  for (const id of Object.keys(counts)) {
    if (counts[id] > 1) push({ severity: "error", category: "Integrity", message: `Duplicate ${label} ID "${id}" appears ${counts[id]} times.`, ref: id });
  }
}

export function runBooksHealthCheck(): HealthIssue[] {
  const issues: HealthIssue[] = [];
  const push = (i: HealthIssue) => issues.push(i);

  const cards  = DB.table("cards");
  const sealed = DB.table("sealed");

  // --- Integrity: duplicate primary keys ---
  dupCheck(cards, "Card Lot ID", "card lot", push);
  dupCheck(sealed, "Lot ID", "sealed lot", push);

  // --- Inventory: negative quantities ---
  for (const c of cards)  if ((Number(c["Qty"]) || 0) < 0) push({ severity: "error", category: "Inventory", message: `Card lot ${c["Card Lot ID"]} has a negative quantity (${c["Qty"]}).`, ref: c["Card Lot ID"] });
  for (const s of sealed) if ((Number(s["Qty"]) || 0) < 0) push({ severity: "error", category: "Inventory", message: `Sealed lot ${s["Lot ID"]} has a negative quantity (${s["Qty"]}).`, ref: s["Lot ID"] });

  // --- Inventory: sold-out lots still carrying value ---
  for (const c of cards) {
    if (isBulkPoolRow(c)) continue;
    if ((Number(c["Qty"]) || 0) <= 0 && (Math.abs(Number(c["Current FMV"]) || 0) > TOL || Math.abs(Number(c["Total Basis"]) || 0) > TOL))
      push({ severity: "warn", category: "Inventory", message: `Card lot ${c["Card Lot ID"]} is sold out (qty 0) but still shows value (FMV ${fmt$(c["Current FMV"])}, basis ${fmt$(c["Total Basis"])}).`, ref: c["Card Lot ID"] });
  }
  for (const s of sealed) {
    if ((Number(s["Qty"]) || 0) <= 0 && (Math.abs(Number(s["Current FMV"]) || 0) > TOL || Math.abs(Number(s["Total Basis"]) || 0) > TOL))
      push({ severity: "warn", category: "Inventory", message: `Sealed lot ${s["Lot ID"]} is sold out (qty 0) but still shows value (FMV ${fmt$(s["Current FMV"])}, basis ${fmt$(s["Total Basis"])}).`, ref: s["Lot ID"] });
  }

  // --- Rips: box cost is conserved (booked basis + held basis === Basis Removed) ---
  for (const rip of DB.table("rips")) {
    const basisRemoved = Number(rip["Basis Removed"]) || 0;
    if (basisRemoved <= 0) continue;
    const ripId = rip["Rip ID"];
    const pulls = cards.filter(c => c["Source ID"] === ripId);
    if (!pulls.length) continue;
    const held = pulls.reduce((a, c) => a + (Number(c["Total Basis"]) || 0), 0);
    const booked = bookedBasisForRipPulls(ripId);
    const diff = +((booked + held) - basisRemoved).toFixed(2);
    if (Math.abs(diff) > Math.max(0.05, basisRemoved * 0.001))
      push({ severity: "error", category: "Rips", message: `Rip ${ripId}: box cost doesn't reconcile. Booked ${fmt$(booked)} + held ${fmt$(held)} = ${fmt$(booked + held)} vs box cost ${fmt$(basisRemoved)} (off ${fmt$(diff)}). Run the rip cost-basis repair in Configs.`, ref: ripId });
  }

  // --- Sales: internal consistency ---
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    const id = sale["Sale ID"];
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    const sumCOGS = items.reduce((a, it) => a + (Number(it["COGS"]) || 0), 0);
    const storedCOGS = Number(sale["COGS"]) || 0;
    if (Math.abs(sumCOGS - storedCOGS) > TOL)
      push({ severity: "warn", category: "Sales", message: `Sale ${id}: stored COGS ${fmt$(storedCOGS)} doesn't match the sum of its line items ${fmt$(sumCOGS)}.`, ref: id });
    const expectNet = +((Number(sale["Subtotal"]) || 0) - (Number(sale["Fees"]) || 0) - (Number(sale["Shipping"]) || 0) - storedCOGS).toFixed(2);
    if (Math.abs(expectNet - (Number(sale["Net Profit"]) || 0)) > TOL)
      push({ severity: "warn", category: "Sales", message: `Sale ${id}: Net Profit ${fmt$(sale["Net Profit"])} doesn't equal subtotal - fees - shipping - COGS (${fmt$(expectNet)}).`, ref: id });
    if (sale["Has Unknown Holding"])
      push({ severity: "info", category: "Tax", message: `Sale ${id} has line(s) with an unknown holding period - this affects the Schedule D / 8949 export.`, ref: id });
  }

  // --- Trades: realized gain ties to its line items ---
  for (const t of DB.table("trades")) {
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    const out: any[] = Array.isArray(t.Outgoing) ? t.Outgoing : [];
    if (!out.length) continue;
    const sumR = out.reduce((a, o) => a + (Number(o["Realized Gain"]) || 0), 0);
    if (Math.abs(sumR - (Number(t["Realized Gain"]) || 0)) > 0.05)
      push({ severity: "info", category: "Trades", message: `Trade ${t["Trade ID"]}: total realized gain ${fmt$(t["Realized Gain"])} doesn't match the sum of its outgoing lines ${fmt$(sumR)}.`, ref: t["Trade ID"] });
  }

  // --- Purchases: totals add up ---
  for (const p of DB.table("purchases")) {
    const id = p["Purchase ID"];
    const items: any[] = Array.isArray(p.Items) ? p.Items : [];
    const sub = items.reduce((a, it) => a + (Number(it["Qty"]) || 0) * (Number(it["UnitCost"]) || 0), 0);
    if (Math.abs(sub - (Number(p["Subtotal"]) || 0)) > TOL)
      push({ severity: "info", category: "Purchases", message: `Purchase ${id}: stored subtotal ${fmt$(p["Subtotal"])} doesn't match its items (${fmt$(sub)}).`, ref: id });
    const tot = +((Number(p["Subtotal"]) || 0) + (Number(p["Fees"]) || 0)).toFixed(2);
    if (Math.abs(tot - (Number(p["Total Paid"]) || 0)) > TOL)
      push({ severity: "info", category: "Purchases", message: `Purchase ${id}: total paid ${fmt$(p["Total Paid"])} doesn't equal subtotal + fees (${fmt$(tot)}).`, ref: id });
  }

  // --- Basis provenance: lot unit basis ties to its source transaction ---
  // (Rip pulls are already covered by the box-cost conservation check above.)
  // Grading capitalizes service fees + shipping into basis; the applied amount
  // is recorded per card, so expected basis accounts for it.
  const gradingApplied = new Map<string, number>();
  for (const g of DB.table("grading")) {
    for (const gc of (Array.isArray(g.Cards) ? g.Cards : []) as any[]) {
      if (gc["Card Lot ID"] && gc["Basis Applied"]) {
        gradingApplied.set(gc["Card Lot ID"], (gradingApplied.get(gc["Card Lot ID"]) || 0) + (Number(gc["Basis Applied Amount"]) || 0));
      }
    }
  }
  const findLot = (id: string) => cards.find(c => c["Card Lot ID"] === id) || sealed.find(s => s["Lot ID"] === id);
  const BASIS_TOL = 0.03; // creation rounds totals then divides — allow a cent of drift

  for (const p of DB.table("purchases")) {
    const items: any[] = Array.isArray(p.Items) ? p.Items : [];
    // Purchase fees are capitalized into lot basis pro-rata by line value
    // (2026-07-11) — the expectation mirrors savePurchase's allocation.
    const pFees = Number(p["Fees"]) || 0;
    const pDenom = items.reduce((a, x) => a + (String(x["Type"] || "").includes("Bulk")
      ? (Number(x["UnitCost"]) || 0)
      : (Number(x["Qty"]) || 0) * (Number(x["UnitCost"]) || 0)), 0);
    for (const it of items) {
      const lotId = String(it["Lot ID"] || "");
      if (!lotId || lotId.includes("BULK")) continue;
      const lot = findLot(lotId);
      if (!lot) continue;
      if (String(lot["Source ID"] || "") !== p["Purchase ID"]) continue; // re-pointed elsewhere
      const qtyPurchased = Math.max(Number(it["Qty"]) || 1, 1);
      const lineValue = (Number(it["UnitCost"]) || 0) * qtyPurchased;
      const feeShare = pFees > 0
        ? (pDenom > 0 ? (lineValue / pDenom) * pFees : pFees / Math.max(items.length, 1))
        : 0;
      const lotQty = Math.max(Number(lot["Qty"]) || 0, 1);
      const expected = +(((lineValue + feeShare) / qtyPurchased) + (gradingApplied.get(lotId) || 0) / lotQty).toFixed(2);
      const actual = +(Number(lot["Unit Basis"]) || 0).toFixed(2);
      if (Math.abs(actual - expected) > BASIS_TOL)
        push({ severity: "warn", category: "Basis", message: `Lot ${lotId}: unit basis ${fmt$(actual)} doesn't match purchase ${p["Purchase ID"]} (expected ${fmt$(expected)}${gradingApplied.has(lotId) ? " incl. capitalized grading fees" : ""}). If this was edited by hand, future sales will book COGS from the edited number.`, ref: lotId });
    }
  }

  for (const t of DB.table("trades")) {
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    const inc: any[] = (Array.isArray(t.Incoming) ? t.Incoming : []).filter((x: any) => x["AsType"] !== "Bulk");
    if (!inc.length) continue;
    const cash = Number(t["Cash"]) || 0;
    const cashPaid = cash < 0 ? -cash : 0;
    const totIncFMV = inc.reduce((a: number, x: any) => a + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
    for (const x of inc) {
      const lotId = String(x["Lot ID"] || "");
      if (!lotId) continue;
      const lot = findLot(lotId);
      if (!lot) continue;
      if (String(lot["Source ID"] || "") !== t["Trade ID"]) continue;
      const qty = Number(x.Qty) || 1;
      const lineFMV = (Number(x.FMV) || 0) * qty;
      const share = cashPaid > 0 ? (totIncFMV > 0 ? (lineFMV / totIncFMV) * cashPaid : cashPaid / inc.length) : 0;
      const lotQty = Math.max(Number(lot["Qty"]) || 0, 1);
      const expected = +(((lineFMV + share) / qty) + (gradingApplied.get(lotId) || 0) / lotQty).toFixed(2);
      const actual = +(Number(lot["Unit Basis"]) || 0).toFixed(2);
      if (Math.abs(actual - expected) > BASIS_TOL)
        push({ severity: "warn", category: "Basis", message: `Lot ${lotId}: unit basis ${fmt$(actual)} doesn't match its allocation from trade ${t["Trade ID"]} (expected ${fmt$(expected)} = FMV share + cash boot${gradingApplied.has(lotId) ? " + capitalized grading fees" : ""}). The trade's realized gain was booked against the original allocation.`, ref: lotId });
    }
  }

  // --- Cash & owner balances ---
  const cash = cashOnHand();
  if (cash < -TOL)
    push({ severity: "warn", category: "Cash", message: `Cash on hand is negative (${fmt$(cash)}). You may be missing income entries, or your starting cash balance isn't set in Configs - Defaults.` });
  const owed = ownerLoanBalance();
  if (owed < -TOL)
    push({ severity: "warn", category: "Owner", message: `Owed to Owner is negative (${fmt$(owed)}) - more has been repaid than was contributed.` });

  // Order: errors, then warns, then info.
  const rank: Record<Severity, number> = { error: 0, warn: 1, info: 2 };
  issues.sort((a, b) => rank[a.severity] - rank[b.severity]);
  return issues;
}

export interface HealthSummary { errors: number; warns: number; infos: number; total: number; }
export function booksHealthSummary(issues?: HealthIssue[]): HealthSummary {
  const list = issues || runBooksHealthCheck();
  return {
    errors: list.filter(i => i.severity === "error").length,
    warns:  list.filter(i => i.severity === "warn").length,
    infos:  list.filter(i => i.severity === "info").length,
    total:  list.length,
  };
}
